import { Colors, Text } from '@amelio/ui-library';
import { useRef } from 'react';

import { AnswerEmojiItem } from './AnswerEmojiItem';

import type { AnswerEmojiListProps, EmojiOption } from './types';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { useArrowNavigation } from '@/hooks/utils/useArrowNavigation';
import { useWindowDimensions } from '@/hooks/utils/useWindowsDimensions';

/**
 * Gap styles mapping for emoji cards spacing
 */
const GAP_STYLES = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
} as const;

/**
 * AnswerEmojiList component
 *
 * A reusable component for displaying emoji options that respondents can select from in surveys.
 * Displays emoji options as selectable cards in a horizontal layout with labels below each emoji.
 *
 * @example
 * ```tsx
 * const feedbackEmojis = [
 *   { id: '1', src: '/emoji/terrible.png', label: 'Very Unsatisfied' },
 *   { id: '2', src: '/emoji/bad.png', label: 'Unsatisfied' },
 *   { id: '3', src: '/emoji/ok.png', label: 'Neutral' },
 *   { id: '4', src: '/emoji/good.png', label: 'Satisfied' },
 *   { id: '5', src: '/emoji/awesome.png', label: 'Very Satisfied' },
 * ];
 *
 * <AnswerEmojiList
 *   emojis={feedbackEmojis}
 *   selectedEmoji={feedbackEmojis[3]}
 *   onEmojiSelect={(emoji) => console.log('Selected:', emoji)}
 * />
 * ```
 */
export const AnswerEmojiList = ({
    emojis = [],
    selectedValues = [],
    onChange,
    className = '',
    disabled = false,
    dataTestId,
    avatarSize = 'md',
    avatarVariant = 'circular',
    labelVariant = 'body-small',
    gap = 'md',
    cardPadding = 'md',
    cardBorderRadius = 'lg',
    unselectedBgColor = 'bg-transparent',
    selectedBorderColor = Colors.PRIMARY,
    unselectedTextColor = Colors.BASE,
}: AnswerEmojiListProps) => {
    // Simple hooks
    const { t } = useAmelioTranslations();
    const { isMobile, width } = useWindowDimensions();
    const isCompact = width > 0 && width < 1200;

    // Simple React hooks
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Responsive overrides for mobile
    const responsiveAvatarSize = isMobile ? '2xs' : avatarSize;
    const responsiveCardPadding = isMobile ? 'sm' : cardPadding;
    const responsiveGap = isMobile ? 'sm' : gap;
    const responsiveLabelVariant = isMobile ? 'caption' : labelVariant;

    // Constants
    const containerStyles = [
        'flex flex-row items-stretch justify-center',
        isCompact && 'flex-wrap',
        GAP_STYLES[responsiveGap],
        className,
    ]
        .filter(Boolean)
        .join(' ');

    // Functions
    const isEmojiSelected = (emoji: EmojiOption): boolean => {
        if (!emoji.id || selectedValues.length === 0) {
            return false;
        }

        return selectedValues.includes(emoji.id);
    };

    const handleEmojiClick = (emoji: EmojiOption) => {
        if (!disabled && onChange && emoji.id) {
            onChange([emoji.id]);
        }
    };

    const handleKeyDown = useArrowNavigation({
        itemCount: emojis?.length ?? 0,
        disabled,
        refs: buttonRefs,
        onNavigate: (index) => {
            if (emojis?.[index] && !disabled && onChange && emojis[index].id) {
                onChange([emojis[index].id]);
            }
        },
    });

    // Handle empty emojis array
    if (!emojis || emojis.length === 0) {
        return (
            <div
                className={containerStyles}
                data-testid={dataTestId}
                role={'radiogroup'}
                aria-label={'Emoji selection'}
            >
                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                >
                    {t('answer.noEmojiOptionsAvailable')}
                </Text>
            </div>
        );
    }

    return (
        <div
            className={containerStyles}
            data-testid={dataTestId}
            role={'radiogroup'}
            aria-label={'Emoji selection'}
        >
            {emojis.map((emoji, index) => {
                const isSelected = isEmojiSelected(emoji);
                const emojiTestId = dataTestId ? `${dataTestId}-emoji-${index}` : undefined;

                return (
                    <AnswerEmojiItem
                        key={emoji.id || index}
                        emoji={emoji}
                        isSelected={isSelected}
                        disabled={disabled}
                        onClick={() => handleEmojiClick(emoji)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        buttonRef={(el) => {
                            buttonRefs.current[index] = el;
                        }}
                        cardPadding={responsiveCardPadding}
                        cardBorderRadius={cardBorderRadius}
                        unselectedBgColor={unselectedBgColor}
                        selectedBorderColor={selectedBorderColor}
                        unselectedTextColor={unselectedTextColor}
                        avatarSize={responsiveAvatarSize}
                        avatarVariant={avatarVariant}
                        labelVariant={responsiveLabelVariant}
                        isMobile={isMobile}
                        dataTestId={emojiTestId}
                    />
                );
            })}
        </div>
    );
};
